import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { 
  Menu, 
  X, 
  ChevronDown, 
  Calculator, 
  Percent, 
  FileText, 
  FileCheck, 
  TrendingUp, 
  Users, 
  Briefcase 
} from 'lucide-react'
import { fetchCollection, getLocalized } from '../lib/cms'

// Map icon names to Lucide components
const iconMap = {
  Calculator,
  Percent,
  FileText,
  FileCheck,
  TrendingUp,
  Users,
  Briefcase
}

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false)
  const [services, setServices] = useState([])
  const location = useLocation()

  const navLinks = [
    { name: 'Forside', path: '/' },
    { name: 'Om Thellefsen Regnskab', path: '/om-os' },
    { name: 'Ydelser', path: '/ydelser' },
    { name: 'Priser', path: '/priser' },
    { name: 'Kontakt', path: '/kontakt' }
  ]

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(path)
  }

  // Fetch services for the dropdown
  useEffect(() => {
    async function loadServices() {
      try {
        const data = await fetchCollection('services')
        setServices(data)
      } catch (error) {
        console.error('Error fetching services for navbar:', error)
      }
    }
    loadServices()
  }, [])

  // Close dropdowns on route change
  useEffect(() => {
    setDropdownOpen(false)
    setMobileServicesOpen(false)
    setIsOpen(false)
  }, [location])

  return (
    <nav className="sticky top-0 z-50 border-b border-stone-200/80 bg-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center gap-3 group">
              <img 
                src="https://assets.auxodev.dk/assets/57eb0739-2c0e-4813-8144-be81ba542e2d/thellefsen-regnskab-logo.png" 
                alt="Thellefsen Regnskab - Revisor og bogholder til fast årlig pris" 
                className="h-10 w-auto object-contain transition-transform group-hover:scale-105"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8 h-full">
            <div className="flex items-center gap-6 h-full">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors duration-200 py-1 border-b-2 ${
                    isActive(link.path)
                      ? 'border-orange-600 text-stone-900 font-semibold'
                      : 'border-transparent text-stone-600 hover:text-stone-900 hover:border-stone-300'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex lg:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              type="button"
              className="inline-flex items-center justify-center text-stone-600 hover:text-stone-900 focus:outline-none"
              aria-controls="mobile-menu"
              aria-expanded="false"
            >
              <span className="sr-only">Åbn menu</span>
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Menu */}
      {isOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          {/* Backdrop overlay */}
          <div 
            className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Sidebar panel */}
          <div className="fixed inset-y-0 right-0 w-full max-w-xs sm:max-w-sm bg-white shadow-2xl z-50 flex flex-col justify-between p-6 transition-transform duration-300 ease-in-out">
            <div>
              {/* Header inside sidebar */}
              <div className="flex items-center justify-between pb-6 border-b border-stone-100">
                <Link to="/" onClick={() => setIsOpen(false)} className="flex items-center gap-3">
                  <img 
                    src="https://assets.auxodev.dk/assets/57eb0739-2c0e-4813-8144-be81ba542e2d/thellefsen-regnskab-logo.png" 
                    alt="Thellefsen Regnskab logo" 
                    className="h-8 w-auto object-contain"
                  />
                </Link>
                <button
                  onClick={() => setIsOpen(false)}
                  type="button"
                  className="text-stone-600 hover:text-stone-900 focus:outline-none"
                >
                  <span className="sr-only">Luk menu</span>
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Navigation links */}
              <div className="py-6 space-y-1.5">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className={`block rounded-none px-3 py-3 text-base font-medium transition-colors ${
                      isActive(link.path)
                        ? 'bg-stone-100 text-stone-900 font-semibold'
                        : 'text-stone-600 hover:bg-stone-50 hover:text-stone-900'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  )
}
